/**
 * Authenticated shell. Renders, top → bottom:
 *   - the OS HUD (always present — points → Score, gift → Rewards, so those are
 *     reachable from the top on every page and need no bottom-nav entry)
 *   - an app context chip (only on body-recomp app pages: /log, /progress)
 *   - the routed page (<Outlet/>)
 *   - the bottom nav — only inside an app ([Home · Log · Progress]); the top
 *     pages (Home, Score, Rewards) have none.
 *
 * Header and content share one centered max-width so the layout reads well on
 * phone and desktop alike.
 */
import { Outlet, useLocation } from "react-router-dom";
import { Dumbbell } from "lucide-react";

import { BottomNav } from "./BottomNav";
import { OsHud } from "./OsHud";

const APP_ROUTES = new Set(["/log", "/progress"]);

function AppContextChip() {
  return (
    <div className="flex items-center gap-2 rounded-[9px] border border-border bg-surface-2 px-2.5 py-1.5 text-[11.5px] font-bold text-foreground">
      <Dumbbell className="size-3.5 text-primary" strokeWidth={1.8} />
      <span>Body-recomp</span>
    </div>
  );
}

export function AppShell() {
  const { pathname } = useLocation();
  const inApp = APP_ROUTES.has(pathname);

  return (
    <div className="flex min-h-dvh flex-col">
      {/* pt-2 is dead weight here — the inline style always wins the cascade
          over a class setting the same property, so the safe-area-aware
          value below is the only one that ever applied. */}
      <header
        className="sticky top-0 z-40 bg-background px-4 sm:px-6 lg:px-8"
        style={{ paddingTop: "calc(var(--safe-top) + 0.5rem)" }}
      >
        <div className="mx-auto w-full max-w-[1400px]">
          <OsHud />
        </div>
      </header>

      {/* extra bottom padding only when the fixed BottomNav is present */}
      <main className={`flex flex-1 flex-col ${inApp ? "pb-20" : "pb-6"}`}>
        {/* The chip lives in normal flow (not the sticky header) so it
            scrolls away with the rest of the page. pt-2 here is the ONE top
            gap below the header — the header itself carries no matching
            pb any more, so this does not double up with it. */}
        {inApp && (
          <div className="mx-auto w-full max-w-[1400px] px-4 pt-2 sm:px-6 lg:px-8">
            <AppContextChip />
          </div>
        )}
        <Outlet />
      </main>

      {inApp && <BottomNav />}
    </div>
  );
}
